import React from 'react'
import { Header } from './components/header'
import { StandardHeaderItems } from './components/standardHeaderItems'
import { User } from '../providers/context'
import { Resizable } from './components/resizable'
import { Button, ButtonShape, ButtonStyle } from '../components/simple/button'
import { IconCaretLeft } from '../icons/library'
import { NoMobileOverlay } from './components/noMobile'

interface LeftPanelProps {
  mobileOpen: boolean
  children: React.ReactNode
  resizable?: boolean
  closeMobile: () => void
  bottom?: React.ReactNode
}

const LeftPanel: React.FC<LeftPanelProps> = ({ children, mobileOpen, closeMobile, bottom, resizable = true }) => {
  const transition = 'z-30 transition-all duration-200 sm:duration-0'
  const position = 'absolute sm:static top-0 left-0 h-full'
  const hideClasses = mobileOpen ? 'bg-charcoal' : '-translate-x-full bg-transparent sm:translate-x-0 sm:bg-charcoal'
  return (
    <div id="tutorial_left" className={`${position} ${transition} ${hideClasses}`}>
      <Resizable
        className={`h-full text-charcoal-text shadow-header`}
        enabled={resizable}
        screenEdgeBuffer={100}
        initialWidth={250}
        minWidth={200}
      >
        {bottom ? (
          <div className="flex h-full w-full flex-col">
            <div className="flex flex-grow flex-col gap-3 overflow-y-auto overflow-x-hidden p-3">{children}</div>
            <div className="mt-auto flex gap-2 p-3">{bottom}</div>
          </div>
        ) : (
          <div className="flex h-full w-full flex-col gap-3 overflow-y-auto overflow-x-hidden p-3">{children}</div>
        )}
        <div
          onClick={closeMobile}
          className={`absolute left-[100%] top-0 -z-10 h-full w-[500px] opacity-50 transition-all duration-200 sm:hidden ${mobileOpen ? 'bg-white' : 'pointer-events-none bg-transparent'}`}
        ></div>
      </Resizable>
    </div>
  )
}

interface MiddlePanelProps {
  children: React.ReactNode
  resizable?: boolean
  blueAbovePanel?: boolean
  className?: string
  initialWidth?: number
}

const MiddlePanel: React.FC<MiddlePanelProps> = ({
  children,
  resizable = true,
  blueAbovePanel,
  className,
  initialWidth = -1,
}) => {
  return (
    <Resizable
      className={`z-10 flex-grow bg-white shadow -sm:min-w-full ${blueAbovePanel ? 'mt-2' : ''} ${className}`}
      enabled={resizable}
      screenEdgeBuffer={100}
      initialWidth={initialWidth}
      minWidth={50}
    >
      <div id="tutorial_middle" className="flex h-full w-full flex-col overflow-hidden">
        {children}
      </div>
    </Resizable>
  )
}

interface RightPanelProps {
  children: React.ReactNode
  blueAbovePanel?: boolean
  rightOpenMobile: boolean
  setRightOpenMobile: (open: boolean) => void
}

const RightPanel: React.FC<RightPanelProps> = ({ children, blueAbovePanel, rightOpenMobile, setRightOpenMobile }) => {
  const [open, setOpen] = React.useState(rightOpenMobile)
  React.useEffect(() => {
    setOpen(rightOpenMobile)
  }, [rightOpenMobile])
  return (
    <div
      className={`absolute flex h-full min-w-0 transition-transform duration-200 sm:h-auto -sm:z-20 ${open ? 'translate-y-full' : 'translate-y-0'} bottom-full left-0 right-0 flex-grow sm:static sm:translate-y-0 ${blueAbovePanel ? 'mt-2' : ''}`}
    >
      <Button
        onClick={() => {
          setOpen(false)
          setTimeout(() => setRightOpenMobile(false), 200)
        }}
        className="absolute left-2 top-[.9rem] z-10 sm:hidden"
        buttonStyle={ButtonStyle.ghostLogoTransparent}
        shape={ButtonShape.iconSmall}
      >
        <IconCaretLeft />
      </Button>
      {children}
    </div>
  )
}

/**
 * ThreePanelLayout is a layout component that provides a three-panel layout with a header, left panel, middle panel, and right panel.
 * These panels can be resized and behave differently on mobile devices.
 *
 * @param left - The content to be displayed in the left panel.
 * @param bottomLeft - The content to be displayed at the bottom of the left panel.
 * @param children - The content to be displayed in the middle panel.
 * @param right - The content to be displayed in the right panel.
 * @param rightOpenMobile - A boolean indicating whether the right panel is current open on mobile devices.
 * @param setRightOpenMobile - Callback function called when the right panel is opened or closed on mobile devices from within the component.
 * @param user - The current user.
 * @param resizable - A boolean indicating whether the panels are resizable. Default is true.
 * @param blueAbovePanels - A boolean indicating whether the panels should have a blue strip above them.
 * @param floating - React nodes to be displayed as a floating element above the layout.
 * @param noMobile - A boolean indicating whether to show a no mobile warning if the window is small.
 * @param headerExtra - Extra elements to be displayed in the header.
 */
export const ThreePanelLayout: React.FC<{
  left?: React.ReactNode
  bottomLeft?: React.ReactNode
  children: React.ReactNode
  right?: React.ReactNode
  rightOpenMobile?: boolean
  setRightOpenMobile?: (open: boolean) => void
  user: User
  resizable?: boolean
  blueAbovePanels?: boolean
  floating?: React.ReactNode
  noMobile?: boolean
  headerExtra?: React.ReactNode
}> = ({
  children,
  user,
  left,
  right,
  rightOpenMobile = false,
  setRightOpenMobile = () => {},
  resizable = true,
  blueAbovePanels = false,
  floating,
  noMobile,
  headerExtra,
  bottomLeft,
}) => {
  const [leftOpen, setLeftOpen] = React.useState(false)

  return (
    <>
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-lightBlue">
        <Header className="print-hide">
          <StandardHeaderItems
            user={user}
            extra={headerExtra}
            onToggleLeft={React.useCallback(() => setLeftOpen((p) => !p), [])}
          />
        </Header>
        <div className="relative flex min-h-0 flex-1 flex-row">
          {left && (
            <LeftPanel
              mobileOpen={leftOpen}
              resizable={resizable}
              closeMobile={() => setLeftOpen(false)}
              bottom={bottomLeft}
            >
              {left}
            </LeftPanel>
          )}
          <MiddlePanel
            blueAbovePanel={blueAbovePanels}
            resizable={resizable && !!right}
            initialWidth={right ? 524 : -1}
          >
            {children}
          </MiddlePanel>
          {right && (
            <RightPanel
              blueAbovePanel={blueAbovePanels}
              rightOpenMobile={rightOpenMobile}
              setRightOpenMobile={setRightOpenMobile}
            >
              {right}
            </RightPanel>
          )}
        </div>
      </div>
      {floating}
      {noMobile && <NoMobileOverlay />}
    </>
  )
}
